"use client";

import Link from "next/link";
import { ArrowRight, Check } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { approach as data } from "@/lib/data/about";

const phaseAccents = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
  "var(--color-emerald)",
] as const;

export function Approach() {
  return (
    <Section id="approach" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-emerald)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {data.phases.map((phase, i) => {
            const accent = phaseAccents[i % phaseAccents.length]!;
            return (
              <Reveal key={phase.number} delay={i * 100}>
                <div
                  className="glass rounded-2xl p-6 h-full relative overflow-hidden"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <div className="flex items-start gap-4">
                    <span
                      className="grid place-items-center h-14 w-14 rounded-2xl shrink-0 font-bold text-lg"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        color: accent,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      {phase.number}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="eyebrow-tiny" style={{ color: accent }}>
                        Phase {phase.number}
                      </div>
                      <h3 className="mt-1">{phase.title}</h3>
                      <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{phase.body}</p>
                    </div>
                  </div>
                  <div className="mt-5 pt-5 border-t border-[rgb(var(--border)/0.12)]">
                    <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Deliverables</div>
                    <ul className="mt-3 space-y-2">
                      {phase.deliverables.map((d) => (
                        <li key={d} className="flex items-center gap-2 text-xs text-[rgb(var(--fg-muted))]">
                          <Check className="h-3.5 w-3.5 shrink-0" style={{ color: accent }} />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={500}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
